import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Copy, Download, Check, Mail, Palette, Type, Image, FileText, Users, Heart, Sparkles, Play, MousePointer, Database, Shield, Zap, Calendar, Camera, CreditCard, FileSignature, User, Activity, ArrowRight, CheckCircle, Clock, Trophy, Target } from 'lucide-react';
import Layout from '@/components/Layout';
import { useToast } from '@/hooks/use-toast';
import LottieAnimation from '@/components/animations/LottieAnimation';

const BrandKit = () => {
  const { toast } = useToast();
  const [copiedColor, setCopiedColor] = useState<string | null>(null);

  const brandColors = [
    { name: 'Cream White', hex: '#FAF8F6', usage: 'Light backgrounds, contrast', textColor: '#5C322B' },
    { name: 'Soft Blush', hex: '#F2D9D3', usage: 'Backgrounds, UI containers', textColor: '#5C322B' },
    { name: 'Rose Clay', hex: '#CDA79D', usage: 'Buttons, links, accents', textColor: '#5C322B' },
    { name: 'Terracotta', hex: '#A55C52', usage: 'Primary brand highlight', textColor: '#FAF8F6' },
    { name: 'Deep Chestnut', hex: '#5C322B', usage: 'Text, icons, headers', textColor: '#FAF8F6' },
    { name: 'Jet Black', hex: '#1A1A1A', usage: 'Dark mode, high contrast', textColor: '#FAF8F6' }
  ];

  const logoAssets = [
    {
      name: 'Primary Logo (Gradient Glow)',
      file: 'juv-logo-primary-glow.webp',
      usage: 'Main website, Hero sections, Luxe print materials',
      formats: ['.webp', '.png']
    },
    {
      name: 'Embossed Logo (Soft Cream on Blush)',
      file: 'juv-logo-soft.webp',
      usage: 'Internal decks, background overlays, UI modals',
      formats: ['.webp', '.png']
    }
  ];

  const copyToClipboard = async (text: string, colorName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedColor(colorName);
      toast({
        title: "Copied to clipboard",
        description: `${colorName} (${text}) copied successfully`,
      });
      setTimeout(() => setCopiedColor(null), 2000);
    } catch (err) {
      toast({
        title: "Copy failed",
        description: "Unable to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  const handleDownload = (filename: string, format: string) => {
    toast({
      title: "Download started",
      description: `Downloading ${filename}${format}`,
    });
  };

  const handleDownloadAll = () => {
    toast({
      title: "Download All started",
      description: "Preparing brand assets ZIP file...",
    });
  };

  const voiceExamples = {
    do: [
      { text: "Glow naturally. Age gracefully.", context: "Hero messaging" },
      { text: "Precision care. Studio experience.", context: "Service descriptions" },
      { text: "Science-backed treatments that feel indulgent.", context: "Treatment copy" }
    ],
    dont: [
      { text: "Look 10 years younger instantly!", context: "Overpromising results" },
      { text: "Zap wrinkles with our laser machine", context: "Clinical language" },
      { text: "DM us for a glow-up lol", context: "Casual social media" }
    ]
  };

  const downloadAssets = [
    { name: 'Logo Pack', format: '.SVG, .PNG', size: '2.4 MB', icon: Image },
    { name: 'Color Palette', format: '.ASE', size: '156 KB', icon: Palette },
    { name: 'Typography Files', format: '.WOFF2, .TTF', size: '890 KB', icon: Type },
    { name: 'Full Brand PDF', format: '.PDF', size: '4.8 MB', icon: FileText }
  ];

  const integrationAreas = [
    {
      area: 'Appointments',
      juvFunction: 'Show upcoming visits in app dashboard',
      patientNowFunction: 'Manage provider calendars',
      integrationType: '1-way sync (read)',
      icon: Calendar,
      color: 'text-blue-600'
    },
    {
      area: 'Treatment History',
      juvFunction: 'Display completed services & notes',
      patientNowFunction: 'Store full chart notes',
      integrationType: '2-way sync (read/write)',
      icon: Activity,
      color: 'text-green-600'
    },
    {
      area: 'Photos',
      juvFunction: 'Before/After preview in app',
      patientNowFunction: 'Store clinical photos',
      integrationType: '1-way push (write)',
      icon: Camera,
      color: 'text-purple-600'
    },
    {
      area: 'Payments & Invoices',
      juvFunction: 'Show credits used & balances',
      patientNowFunction: 'Process and record transactions',
      integrationType: '1-way sync (read)',
      icon: CreditCard,
      color: 'text-juv-terracotta'
    },
    {
      area: 'Consent Forms',
      juvFunction: 'Offer digital sign in app',
      patientNowFunction: 'Store forms and documentation',
      integrationType: '1-way push (write)',
      icon: FileSignature,
      color: 'text-indigo-600'
    },
    {
      area: 'Patient Profile',
      juvFunction: 'Enhance with wellness goals & credits',
      patientNowFunction: 'Manage demographic info',
      integrationType: 'SSO + sync',
      icon: User,
      color: 'text-emerald-600'
    }
  ];

  const juvAppFeatures = [
    {
      icon: User,
      title: 'Patient Dashboard',
      description: 'View bookings, track credits, refer friends, upload photos'
    },
    {
      icon: Users,
      title: 'Staff Dashboard',
      description: 'Check in members, see credit eligibility, log treatments'
    },
    {
      icon: Zap,
      title: 'AI Agents',
      description: 'Auto-generate treatment notes, send follow-ups, recommend next steps'
    },
    {
      icon: CreditCard,
      title: 'Credit Wallet',
      description: 'Glow credit system synced to member plans'
    },
    {
      icon: Camera,
      title: 'Before/After Tracker',
      description: 'Visual history synced with PatientNow'
    },
    {
      icon: CheckCircle,
      title: 'Consent Collection',
      description: 'Digital forms auto-submitted to EMR'
    }
  ];

  const roadmapPhases = {
    phase1: [
      'Sync patient records (name, phone, appointment date)',
      'Log treatments and upload photos',
      'Submit signed consents to PatientNow'
    ],
    phase2: [
      'Two-way sync for invoices and payments',
      'Real-time staff alerts',
      'Deep link access from JUV app to PatientNow chart',
      'Treatment plan sync across both systems'
    ]
  };

  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const competitiveComparison = [
    {
      feature: 'Patient Wallet & Credits',
      repeatMD: '✅ Loyalty points',
      kleer: '❌ None',
      moxie: '❌ None',
      juv: '✅ Glow Credits with usage tracking'
    },
    {
      feature: 'Patient Dashboard',
      repeatMD: '✅ Branded App',
      kleer: '❌ Web only',
      moxie: '❌ None',
      juv: '✅ Mobile-first portal with photo log'
    },
    {
      feature: 'EMR Integration',
      repeatMD: '❌ None',
      kleer: '❌ None',
      moxie: '❌ None',
      juv: '✅ PatientNow 2-way sync (Phase 2)'
    },
    {
      feature: 'Before/After Gallery',
      repeatMD: '✅ Limited',
      kleer: '❌ None',
      moxie: '❌ None',
      juv: '✅ Uploads, AI match, and tracking'
    },
    {
      feature: 'Consent & Forms',
      repeatMD: '❌',
      kleer: '❌',
      moxie: '✅',
      juv: '✅ Digitally submitted to EMR'
    },
    {
      feature: 'Staff Dashboard',
      repeatMD: '❌',
      kleer: '❌',
      moxie: '✅ Checklists',
      juv: '✅ Redemptions + check-ins'
    },
    {
      feature: 'SOP / LMS Training',
      repeatMD: '❌',
      kleer: '❌',
      moxie: '✅ Protocols',
      juv: '✅ Alma LMS + Launch Kits'
    },
    {
      feature: 'AI Assistant Layer',
      repeatMD: '❌',
      kleer: '❌',
      moxie: '❌',
      juv: '✅ Miles, Devon, Alma, Giselle'
    },
    {
      feature: 'Membership Billing',
      repeatMD: '❌',
      kleer: '✅ Subscriptions',
      moxie: '❌',
      juv: '✅ $99/mo + Beauty Bank credit system'
    },
    {
      feature: 'Multi-location Scalability',
      repeatMD: '✅ Enterprise Ready',
      kleer: '✅ Available',
      moxie: '✅ Available',
      juv: '✅ Franchise-ready with HQ Portal'
    }
  ];

  return (
    <Layout showCTA={false}>
      <div className="min-h-screen bg-juv-cream-white">
        {/* Enhanced Hero Section */}
        <section className="pt-24 pb-20 bg-gradient-to-br from-juv-cream-white to-juv-soft-blush relative overflow-hidden">
          {/* Lottie Background Animation */}
          <div className="absolute inset-0 flex items-center justify-center opacity-20 pointer-events-none">
            <LottieAnimation
              loop={true}
              autoplay={true}
              className="w-96 h-96"
            />
          </div>
          
          <div className="container mx-auto px-4 text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto"
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-juv-terracotta to-juv-deep-chestnut rounded-full flex items-center justify-center shadow-glow animate-glow-pulse">
                  <Palette className="w-8 h-8 text-white" />
                </div>
              </div>
              
              <h1 className="apple-headline text-juv-deep-chestnut mb-6">
                JUV Brand Kit
              </h1>
              
              <p className="apple-subheadline text-juv-deep-chestnut/70 mb-8 max-w-3xl mx-auto">
                These brand assets represent the modern, calming, and elevated aesthetic of JUV. Use these logos, colors, and guidelines to ensure visual consistency across all locations, campaigns, and collateral.
              </p>
            </motion.div>
          </div>
        </section>

        {/* NEW: Official Brand Assets Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-juv-deep-chestnut mb-4">✨ Official Brand Assets</h2>
                <p className="text-juv-deep-chestnut/70 max-w-3xl mx-auto">
                  These brand assets represent the modern, calming, and elevated aesthetic of JUV.
                  Use these logos, colors, and guidelines to ensure visual consistency across all locations, campaigns, and collateral.
                </p>
              </div>

              {/* Logo Files Section */}
              <div className="mb-16">
                <h3 className="text-2xl font-semibold text-juv-deep-chestnut mb-8 flex items-center gap-2">
                  🔶 Logo Files
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  {logoAssets.map((logo, index) => (
                    <Card key={logo.name} className="glass-card bg-juv-cream-white hover:shadow-glow transition-all duration-300 group">
                      <CardContent className="p-6">
                        <div className="text-center mb-6">
                          <div className="h-32 bg-gradient-to-br from-juv-soft-blush to-juv-rose-clay rounded-lg flex items-center justify-center mb-4 group-hover:animate-sparkle">
                            <div className="text-3xl font-bold text-juv-deep-chestnut">JUV</div>
                          </div>
                          <h4 className="font-semibold text-juv-deep-chestnut mb-2">{logo.name}</h4>
                          <p className="text-sm text-juv-deep-chestnut/60 mb-1">📁 File: {logo.file}</p>
                          <p className="text-sm text-juv-terracotta mb-4">🟠 Usage: {logo.usage}</p>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 justify-center">
                          {logo.formats.map((format) => (
                            <Button 
                              key={format}
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDownload(logo.file, format)}
                              className="border-juv-rose-clay text-juv-deep-chestnut hover:bg-juv-soft-blush"
                            >
                              <Download className="w-3 h-3 mr-1" />
                              Download {format}
                            </Button>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <Card className="glass-card bg-juv-cream-white border-juv-rose-clay/20">
                  <CardContent className="p-6 text-center">
                    <p className="text-sm text-juv-deep-chestnut/80 mb-4">
                      📝 Both logos maintain clean sans-serif proportions with subtle radial glow to evoke rejuvenation and warmth.
                    </p>
                    <Button 
                      onClick={handleDownloadAll}
                      className="apple-button-primary bg-juv-terracotta hover:bg-juv-deep-chestnut"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download All Assets (ZIP)
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Enhanced Color Palette */}
              <div className="mb-16">
                <h3 className="text-2xl font-semibold text-juv-deep-chestnut mb-8 flex items-center gap-2">
                  🎨 Color Palette
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  {brandColors.map((color, index) => (
                    <motion.div
                      key={color.name}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <Card className="overflow-hidden hover:shadow-glow transition-all duration-300 bg-white group hover:scale-105">
                        <div 
                          className="h-24 w-full relative overflow-hidden"
                          style={{ backgroundColor: color.hex }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out"></div>
                        </div>
                        <CardContent className="p-4">
                          <h4 className="font-semibold text-juv-deep-chestnut mb-2">{color.name}</h4>
                          <div className="flex items-center justify-between mb-2">
                            <code className="text-sm bg-gray-100 px-2 py-1 rounded text-juv-deep-chestnut font-mono">
                              {color.hex}
                            </code>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(color.hex, color.name)}
                              className="h-8 w-8 p-0 hover:animate-sparkle"
                            >
                              {copiedColor === color.name ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                          <p className="text-sm text-juv-deep-chestnut/60">{color.usage}</p>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Typography Section */}
              <div className="mb-16">
                <h3 className="text-2xl font-semibold text-juv-deep-chestnut mb-8 flex items-center gap-2">
                  📐 Typography
                </h3>
                
                <Card className="glass-card bg-juv-cream-white border-juv-rose-clay/20">
                  <CardContent className="p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="font-semibold text-juv-terracotta mb-4">Primary Font Family</h4>
                        <div className="space-y-4">
                          <div>
                            <p className="text-2xl font-bold text-juv-deep-chestnut mb-1">Inter Bold</p>
                            <p className="text-sm text-juv-deep-chestnut/60">Usage: Titles, Headings</p>
                          </div>
                          <div>
                            <p className="text-lg font-medium text-juv-deep-chestnut mb-1">Inter Medium</p>
                            <p className="text-sm text-juv-deep-chestnut/60">Usage: UI Elements, Labels</p>
                          </div>
                          <div>
                            <p className="text-base font-normal text-juv-deep-chestnut mb-1">Inter Regular</p>
                            <p className="text-sm text-juv-deep-chestnut/60">Usage: Body Text, Paragraphs</p>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-juv-terracotta mb-4">Backup & Fallbacks</h4>
                        <div className="space-y-3">
                          <div>
                            <p className="font-medium text-juv-deep-chestnut">Backup: Helvetica Neue</p>
                            <p className="text-sm text-juv-deep-chestnut/60">Fallback for older systems</p>
                          </div>
                          <div>
                            <p className="font-medium text-juv-deep-chestnut">System: Sans-serif</p>
                            <p className="text-sm text-juv-deep-chestnut/60">Universal fallback</p>
                          </div>
                        </div>
                        
                        <div className="mt-6 p-4 bg-juv-soft-blush/30 rounded-lg">
                          <p className="text-sm text-juv-deep-chestnut">
                            <strong>CSS Declaration:</strong><br />
                            <code className="text-xs">font-family: 'Inter', 'Helvetica Neue', sans-serif;</code>
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Usage Guidelines */}
              <div>
                <h3 className="text-2xl font-semibold text-juv-deep-chestnut mb-8">📋 Usage Guidelines</h3>
                
                <Card className="glass-card bg-juv-cream-white border-juv-rose-clay/20">
                  <CardContent className="p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="font-semibold text-green-600 mb-4 flex items-center gap-2">
                          <Check className="w-5 h-5" />
                          Do's
                        </h4>
                        <ul className="space-y-2 text-sm text-juv-deep-chestnut">
                          <li>• Use logos on clean, minimal backgrounds</li>
                          <li>• Maintain proper clear space around logos</li>
                          <li>• Keep original proportions intact</li>
                          <li>• Use specified color palette consistently</li>
                          <li>• Follow typography hierarchy guidelines</li>
                          <li>• Test contrast ratios for accessibility</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-red-600 mb-4 flex items-center gap-2">
                          <span className="text-lg">✗</span>
                          Don'ts
                        </h4>
                        <ul className="space-y-2 text-sm text-juv-deep-chestnut">
                          <li>• Don't stretch, rotate, or skew logos</li>
                          <li>• Don't use on busy or low-contrast backgrounds</li>
                          <li>• Don't alter colors or add effects</li>
                          <li>• Don't recreate or modify letterforms</li>
                          <li>• Don't use below minimum size requirements</li>
                          <li>• Don't combine with competing brand elements</li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Brand DNA Section */}
        <section className="py-16 bg-juv-soft-blush/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-juv-deep-chestnut mb-4">Our DNA</h2>
              </div>
              
              <Card className="glass-card bg-juv-cream-white border-juv-rose-clay/20">
                <CardContent className="p-8 text-center">
                  <p className="apple-body text-juv-deep-chestnut leading-relaxed mb-6">
                    JUV is a self-care destination built for the modern aesthetic patient.
                  </p>
                  <p className="apple-body text-juv-deep-chestnut leading-relaxed mb-6">
                    Rooted in science, powered by beauty — we make looking and feeling your best feel effortless.
                  </p>
                  <p className="apple-body text-juv-deep-chestnut/80 font-medium">
                    The brand is clean, elegant, minimal, and confident. Never sterile. Never loud. Always warm.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Enhanced Color Palette Section */}
        <section className="py-16 bg-juv-soft-blush/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-juv-deep-chestnut mb-4">Color Palette</h2>
              <p className="text-juv-deep-chestnut/70 max-w-2xl mx-auto">
                Use Cream White as the base for backgrounds. Accent with Blush, Clay, and Terra.
                Avoid bright colors or stark contrasts — the mood is refined, not clinical.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {brandColors.map((color, index) => (
                <motion.div
                  key={color.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="overflow-hidden hover:shadow-glow transition-all duration-300 bg-white group hover:scale-105">
                    <div 
                      className="h-32 w-full relative overflow-hidden"
                      style={{ backgroundColor: color.hex }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out"></div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-juv-deep-chestnut mb-2">{color.name}</h3>
                      <div className="flex items-center justify-between mb-2">
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded text-juv-deep-chestnut font-mono">
                          {color.hex}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(color.hex, color.name)}
                          className="h-8 w-8 p-0 hover:animate-sparkle"
                        >
                          {copiedColor === color.name ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <p className="text-sm text-juv-deep-chestnut/60">{color.usage}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Enhanced Typography Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-juv-deep-chestnut mb-4">Typography</h2>
              <p className="text-juv-deep-chestnut/70 max-w-2xl mx-auto">
                Large, confident display text for headings with generous spacing and line height.
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-8">
              <Card className="glass-card bg-juv-cream-white border-juv-rose-clay/20">
                <CardHeader>
                  <CardTitle className="text-juv-deep-chestnut">Primary Fonts</CardTitle>
                </CardHeader>
                <CardContent className="space-y-8">
                  <div>
                    <h3 className="text-sm font-medium text-juv-deep-chestnut/60 mb-2">DISPLAY</h3>
                    <p className="text-4xl font-serif text-juv-deep-chestnut mb-2">Playfair Display</p>
                    <p className="text-sm text-juv-deep-chestnut/70">For headings and hero text</p>
                    <p className="text-xs text-juv-deep-chestnut/50 mt-1">Display: 48px / 1.2 line height</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-juv-deep-chestnut/60 mb-2">BODY</h3>
                    <p className="text-lg font-sans text-juv-deep-chestnut mb-2 leading-relaxed">Inter</p>
                    <p className="text-sm text-juv-deep-chestnut/70">For body text and UI elements</p>
                    <p className="text-xs text-juv-deep-chestnut/50 mt-1">Body: 18px / 1.5 line height</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-card bg-juv-cream-white border-juv-rose-clay/20">
                <CardHeader>
                  <CardTitle className="text-juv-deep-chestnut">Typography Scale</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div>
                      <h1 className="apple-headline text-juv-deep-chestnut">Heading 1 - Large Display</h1>
                      <p className="text-xs text-juv-deep-chestnut/50">48px / 1.2 line height / -0.025em letter spacing</p>
                    </div>
                    <div>
                      <h2 className="text-3xl font-semibold text-juv-deep-chestnut">Heading 2 - Medium Display</h2>
                      <p className="text-xs text-juv-deep-chestnut/50">36px / 1.3 line height / -0.022em letter spacing</p>
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold text-juv-deep-chestnut">Heading 3 - Small Display</h3>
                      <p className="text-xs text-juv-deep-chestnut/50">24px / 1.4 line height / -0.019em letter spacing</p>
                    </div>
                    <div>
                      <p className="apple-body text-juv-deep-chestnut">Body Large - For important body text and descriptions</p>
                      <p className="text-xs text-juv-deep-chestnut/50">18px / 1.6 line height / -0.011em letter spacing</p>
                    </div>
                    <div>
                      <p className="text-base text-juv-deep-chestnut">Body Regular - Standard body text with optimal readability</p>
                      <p className="text-xs text-juv-deep-chestnut/50">16px / 1.5 line height / -0.011em letter spacing</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Enhanced Logo Usage Section */}
        <section className="py-16 bg-juv-soft-blush/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-juv-deep-chestnut mb-4">Logo Usage</h2>
              <p className="text-juv-deep-chestnut/70 max-w-2xl mx-auto">
                Clear space guidelines and proper usage examples for the JUV logo system.
              </p>
            </div>

            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <Card className="bg-white hover:shadow-apple-lg transition-all duration-300">
                  <CardHeader>
                    <CardTitle className="text-juv-deep-chestnut text-center">Primary Logo - Light</CardTitle>
                  </CardHeader>
                  <CardContent className="p-8 text-center bg-white">
                    <div className="h-24 flex items-center justify-center border-2 border-dashed border-juv-rose-clay/30 rounded-lg mb-4">
                      <div className="text-4xl font-bold text-juv-deep-chestnut">JUV</div>
                    </div>
                    <p className="text-sm text-juv-deep-chestnut/60 mb-2">Use on light backgrounds</p>
                    <Button variant="outline" size="sm" className="text-xs">
                      <Download className="w-3 h-3 mr-1" />
                      Download
                    </Button>
                  </CardContent>
                </Card>

                <Card className="bg-juv-deep-chestnut hover:shadow-apple-lg transition-all duration-300">
                  <CardHeader>
                    <CardTitle className="text-white text-center">Primary Logo - Dark</CardTitle>
                  </CardHeader>
                  <CardContent className="p-8 text-center bg-juv-deep-chestnut">
                    <div className="h-24 flex items-center justify-center border-2 border-dashed border-white/30 rounded-lg mb-4">
                      <div className="text-4xl font-bold text-white">JUV</div>
                    </div>
                    <p className="text-sm text-white/60 mb-2">Use on dark backgrounds</p>
                    <Button variant="outline" size="sm" className="text-xs border-white/30 text-white hover:bg-white/10">
                      <Download className="w-3 h-3 mr-1" />
                      Download
                    </Button>
                  </CardContent>
                </Card>

                <Card className="bg-juv-cream-white hover:shadow-apple-lg transition-all duration-300">
                  <CardHeader>
                    <CardTitle className="text-juv-deep-chestnut text-center">Icon Only</CardTitle>
                  </CardHeader>
                  <CardContent className="p-8 text-center bg-juv-cream-white">
                    <div className="h-24 flex items-center justify-center border-2 border-dashed border-juv-rose-clay/30 rounded-lg mb-4">
                      <div className="w-12 h-12 bg-juv-terracotta rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-xl">J</span>
                      </div>
                    </div>
                    <p className="text-sm text-juv-deep-chestnut/60 mb-2">Social media & favicons</p>
                    <Button variant="outline" size="sm" className="text-xs">
                      <Download className="w-3 h-3 mr-1" />
                      Download
                    </Button>
                  </CardContent>
                </Card>
              </div>

              <Card className="glass-card bg-juv-cream-white border-juv-rose-clay/20">
                <CardHeader>
                  <CardTitle className="text-juv-deep-chestnut">Usage Guidelines & Clear Space</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold text-green-600 mb-3 flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        Do
                      </h4>
                      <ul className="space-y-2 text-sm text-juv-deep-chestnut">
                        <li>• Use white logo on dark backgrounds</li>
                        <li>• Maintain X-height clear space around logo</li>
                        <li>• Keep proportions intact at all sizes</li>
                        <li>• Use on elegant, minimal backdrops</li>
                        <li>• Minimum size: 24px for digital, 0.5" for print</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-red-600 mb-3 flex items-center gap-2">
                        <span className="text-lg">✗</span>
                        Don't
                      </h4>
                      <ul className="space-y-2 text-sm text-juv-deep-chestnut">
                        <li>• Stretch, rotate, or add shadows</li>
                        <li>• Place on busy or low-contrast backgrounds</li>
                        <li>• Use below minimum size requirements</li>
                        <li>• Alter colors or add transparency</li>
                        <li>• Recreate or modify the letterforms</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* New Iconography & Motion Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-juv-deep-chestnut mb-4">Iconography & Motion</h2>
              <p className="text-juv-deep-chestnut/70 max-w-2xl mx-auto">
                Thoughtful animation enhances the user experience without overwhelming the content.
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <Card className="glass-card bg-juv-cream-white border-juv-rose-clay/20">
                  <CardHeader>
                    <CardTitle className="text-juv-deep-chestnut flex items-center gap-2">
                      <Play className="w-5 h-5 text-juv-terracotta" />
                      Animated Icons
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-center py-8">
                    <div className="flex justify-center gap-6 mb-6">
                      <Sparkles className="w-8 h-8 text-juv-terracotta animate-sparkle" />
                      <Heart className="w-8 h-8 text-juv-terracotta animate-glow-pulse" />
                      <Palette className="w-8 h-8 text-juv-terracotta hover:animate-sparkle transition-all duration-300 cursor-pointer" />
                    </div>
                    <p className="text-sm text-juv-deep-chestnut/70">
                      Use animated icons for app UI and digital onboarding
                    </p>
                  </CardContent>
                </Card>

                <Card className="glass-card bg-juv-cream-white border-juv-rose-clay/20">
                  <CardHeader>
                    <CardTitle className="text-juv-deep-chestnut flex items-center gap-2">
                      <MousePointer className="w-5 h-5 text-juv-terracotta" />
                      Static Icons
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-center py-8">
                    <div className="flex justify-center gap-6 mb-6">
                      <Image className="w-8 h-8 text-juv-terracotta" />
                      <FileText className="w-8 h-8 text-juv-terracotta" />
                      <Type className="w-8 h-8 text-juv-terracotta" />
                    </div>
                    <p className="text-sm text-juv-deep-chestnut/70">
                      Use static icons for print materials and tooltips
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Card className="glass-card bg-juv-cream-white border-juv-rose-clay/20">
                <CardHeader>
                  <CardTitle className="text-juv-deep-chestnut">Motion Guidelines</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-juv-terracotta mb-3">Timing & Easing</h4>
                      <ul className="space-y-2 text-sm text-juv-deep-chestnut">
                        <li>• Use Apple HIG timing functions</li>
                        <li>• Subtle animations: 200-300ms</li>
                        <li>• Page transitions: 400-600ms</li>
                        <li>• Respect reduced motion preferences</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-juv-terracotta mb-3">Animation Principles</h4>
                      <ul className="space-y-2 text-sm text-juv-deep-chestnut">
                        <li>• Enhance, don't distract</li>
                        <li>• Maintain brand warmth</li>
                        <li>• Use gentle easing curves</li>
                        <li>• Keep interactions responsive</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Enhanced Brand Voice Section */}
        <section className="py-16 bg-juv-soft-blush/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-juv-deep-chestnut mb-4">Brand Voice</h2>
              <p className="text-juv-deep-chestnut/70 max-w-2xl mx-auto">
                Calm, confident, intelligent. Speaks directly to self-aware, wellness-savvy adults.
                Avoid jargon. Use natural, elevated language.
              </p>
            </div>

            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
              <Card className="glass-card bg-green-50 border-green-200">
                <CardHeader>
                  <CardTitle className="text-green-700 flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    Voice Do's
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {voiceExamples.do.map((example, index) => (
                      <div key={index} className="p-4 bg-white rounded-lg border border-green-200 hover:shadow-apple transition-shadow duration-300">
                        <p className="text-juv-deep-chestnut italic apple-body mb-2">"{example.text}"</p>
                        <p className="text-xs text-green-600 font-medium">{example.context}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-card bg-red-50 border-red-200">
                <CardHeader>
                  <CardTitle className="text-red-700 flex items-center gap-2">
                    <span className="text-lg">✗</span>
                    Voice Don'ts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {voiceExamples.dont.map((example, index) => (
                      <div key={index} className="p-4 bg-white rounded-lg border border-red-200 hover:shadow-apple transition-shadow duration-300">
                        <p className="text-juv-deep-chestnut italic line-through opacity-60 apple-body mb-2">"{example.text}"</p>
                        <p className="text-xs text-red-600 font-medium">{example.context}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Technology Integration Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-juv-deep-chestnut mb-4">Technology Integration</h2>
              <p className="text-juv-deep-chestnut/70 max-w-3xl mx-auto">
                At JUV, we believe that medical records should do more than store data — they should empower the patient journey across every step of care.
              </p>
            </div>

            <div className="max-w-6xl mx-auto space-y-12">
              {/* ERM Strategy Hero */}
              <Card className="glass-card bg-juv-cream-white border-juv-rose-clay/20">
                <CardContent className="p-8 text-center">
                  <Database className="w-12 h-12 text-juv-terracotta mx-auto mb-4 animate-glow-pulse" />
                  <h3 className="text-2xl font-semibold text-juv-deep-chestnut mb-4">Smart ERM Integration</h3>
                  <p className="apple-body text-juv-deep-chestnut leading-relaxed max-w-3xl mx-auto">
                    We don't aim to reinvent the EMR. We're building a smart, member-first interface that works with PatientNow to deliver the modern medspa experience patients expect.
                  </p>
                </CardContent>
              </Card>

              {/* Integration Areas Grid */}
              <div>
                <h3 className="text-2xl font-semibold text-juv-deep-chestnut mb-8 text-center">How We Integrate with PatientNow</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {integrationAreas.map((area, index) => {
                    const IconComponent = area.icon;
                    return (
                      <motion.div
                        key={area.area}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        <Card className="glass-card bg-white hover:shadow-glow transition-all duration-300 group h-full">
                          <CardContent className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                              <div className="w-10 h-10 bg-juv-soft-blush rounded-lg flex items-center justify-center group-hover:animate-sparkle">
                                <IconComponent className={`w-5 h-5 ${area.color}`} />
                              </div>
                              <h4 className="font-semibold text-juv-deep-chestnut">{area.area}</h4>
                            </div>
                            
                            <div className="space-y-3">
                              <div className="bg-juv-soft-blush/30 p-3 rounded-lg">
                                <p className="text-xs font-medium text-juv-terracotta mb-1">JUV Function</p>
                                <p className="text-sm text-juv-deep-chestnut">{area.juvFunction}</p>
                              </div>
                              
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <p className="text-xs font-medium text-gray-600 mb-1">PatientNow Function</p>
                                <p className="text-sm text-juv-deep-chestnut">{area.patientNowFunction}</p>
                              </div>
                              
                              <div className="text-center">
                                <span className="inline-flex items-center gap-1 px-3 py-1 bg-juv-terracotta/10 text-juv-terracotta rounded-full text-xs font-medium">
                                  <ArrowRight className="w-3 h-3" />
                                  {area.integrationType}
                                </span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* JUV App Features */}
              <div>
                <h3 className="text-2xl font-semibold text-juv-deep-chestnut mb-8 text-center">What the JUV App Actually Does</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {juvAppFeatures.map((feature, index) => {
                    const IconComponent = feature.icon;
                    return (
                      <Card key={feature.title} className="glass-card bg-juv-cream-white hover:shadow-glow transition-all duration-300 group">
                        <CardContent className="p-6 text-center">
                          <div className="w-12 h-12 bg-juv-terracotta/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:animate-sparkle">
                            <IconComponent className="w-6 h-6 text-juv-terracotta" />
                          </div>
                          <h4 className="font-semibold text-juv-deep-chestnut mb-2">{feature.title}</h4>
                          <p className="text-sm text-juv-deep-chestnut/70">{feature.description}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>

              {/* Integration Roadmap */}
              <Card className="glass-card bg-juv-cream-white border-juv-rose-clay/20">
                <CardHeader>
                  <CardTitle className="text-juv-deep-chestnut text-center">Integration Roadmap</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <h4 className="font-semibold text-green-700">Phase 1 - Live</h4>
                      </div>
                      <ul className="space-y-2">
                        {roadmapPhases.phase1.map((item, index) => (
                          <li key={index} className="flex items-center gap-2 text-sm text-juv-deep-chestnut">
                            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <Clock className="w-5 h-5 text-blue-600" />
                        </div>
                        <h4 className="font-semibold text-blue-700">Phase 2 - Q3 Launch</h4>
                      </div>
                      <ul className="space-y-2">
                        {roadmapPhases.phase2.map((item, index) => (
                          <li key={index} className="flex items-center gap-2 text-sm text-juv-deep-chestnut">
                            <Clock className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* HIPAA Compliance Badge */}
              <Card className="glass-card bg-juv-cream-white border-juv-rose-clay/20">
                <CardContent className="p-8 text-center">
                  <Shield className="w-12 h-12 text-juv-terracotta mx-auto mb-4 animate-glow-pulse" />
                  <h3 className="text-xl font-semibold text-juv-deep-chestnut mb-2">HIPAA-Compliant, Future-Proof</h3>
                  <p className="text-juv-deep-chestnut/70 max-w-2xl mx-auto">
                    JUV is built to meet the highest standards of data security and patient privacy. All integrations with PatientNow are HIPAA-compliant and designed for scale — from one location to one hundred.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Why JUV Wins Competitive Comparison Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            {/* Hero Block */}
            <div className="bg-juv-cream-white py-12 px-6 md:px-20 text-center rounded-lg mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-juv-terracotta to-juv-deep-chestnut rounded-full flex items-center justify-center shadow-glow animate-glow-pulse">
                    <Trophy className="w-8 h-8 text-white" />
                  </div>
                </div>
                
                <h2 className="text-3xl font-bold text-juv-deep-chestnut mb-6">Why JUV Wins</h2>
                <p className="apple-body text-juv-deep-chestnut leading-relaxed max-w-3xl mx-auto">
                  We didn't build another loyalty app, billing portal, or checklist tool.
                  <br />
                  We built the entire ecosystem — and that's why JUV doesn't compete.
                  <br />
                  <span className="font-semibold text-juv-terracotta">It dominates.</span>
                </p>
              </motion.div>
            </div>

            {/* Competitive Comparison Table */}
            <div className="max-w-7xl mx-auto">
              <Card className="glass-card bg-white border-juv-rose-clay/20 overflow-hidden">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table className="w-full">
                      <TableHeader>
                        <TableRow className="bg-juv-soft-blush hover:bg-juv-soft-blush">
                          <TableHead className="py-4 px-6 font-semibold text-juv-deep-chestnut min-w-[200px]">Feature</TableHead>
                          <TableHead className="py-4 px-6 font-semibold text-juv-deep-chestnut min-w-[150px] text-center">RepeatMD</TableHead>
                          <TableHead className="py-4 px-6 font-semibold text-juv-deep-chestnut min-w-[150px] text-center">Kleer</TableHead>
                          <TableHead className="py-4 px-6 font-semibold text-juv-deep-chestnut min-w-[150px] text-center">Moxie</TableHead>
                          <TableHead className="py-4 px-6 font-semibold text-juv-terracotta min-w-[200px] text-center bg-juv-terracotta/10">
                            JUV 🧠
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {competitiveComparison.map((row, index) => (
                          <TableRow 
                            key={row.feature} 
                            className="hover:bg-juv-cream-white/50 transition-colors duration-200"
                          >
                            <TableCell className="py-4 px-6 font-medium text-juv-deep-chestnut">
                              {row.feature}
                            </TableCell>
                            <TableCell className="py-4 px-6 text-center text-juv-deep-chestnut">
                              {row.repeatMD}
                            </TableCell>
                            <TableCell className="py-4 px-6 text-center text-juv-deep-chestnut">
                              {row.kleer}
                            </TableCell>
                            <TableCell className="py-4 px-6 text-center text-juv-deep-chestnut">
                              {row.moxie}
                            </TableCell>
                            <TableCell className="py-4 px-6 text-center font-medium text-juv-terracotta bg-juv-terracotta/5">
                              {row.juv}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Bottom Line Callout */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mt-12 text-center px-6 md:px-20"
            >
              <Card className="glass-card bg-juv-cream-white border-juv-rose-clay/20">
                <CardContent className="p-8">
                  <Target className="w-12 h-12 text-juv-terracotta mx-auto mb-6 animate-glow-pulse" />
                  <h3 className="text-2xl font-semibold text-juv-deep-chestnut mb-6">The Bottom Line</h3>
                  <div className="space-y-4 text-juv-deep-chestnut">
                    <p className="apple-body">RepeatMD is a rewards app.</p>
                    <p className="apple-body">Kleer is a billing tool.</p>
                    <p className="apple-body">Moxie is a checklist system.</p>
                  </div>
                  <div className="mt-8 p-6 bg-juv-terracotta/10 rounded-lg">
                    <p className="text-xl font-semibold text-juv-terracotta">
                      💡 JUV is the operating system — for modern medspas.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* Enhanced Download Assets Section */}
        <section className="py-16 bg-juv-soft-blush/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-juv-deep-chestnut mb-4">Download Assets</h2>
              <p className="text-juv-deep-chestnut/70 max-w-2xl mx-auto">
                Access logo files, color palettes, and brand guidelines for your projects.
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {downloadAssets.map((asset, index) => {
                  const IconComponent = asset.icon;
                  return (
                    <Card key={asset.name} className="glass-card bg-white hover:shadow-glow transition-all duration-300 group">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-juv-terracotta/10 rounded-lg flex items-center justify-center group-hover:animate-sparkle">
                              <IconComponent className="w-6 h-6 text-juv-terracotta" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-juv-deep-chestnut">{asset.name}</h3>
                              <p className="text-sm text-juv-deep-chestnut/60">{asset.format} • {asset.size}</p>
                            </div>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="border-juv-rose-clay text-juv-deep-chestnut hover:bg-juv-soft-blush group-hover:animate-sparkle"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <Card className="glass-card bg-juv-cream-white border-juv-rose-clay/20">
                <CardContent className="p-8 text-center">
                  <Users className="w-12 h-12 text-juv-terracotta mx-auto mb-4 animate-glow-pulse" />
                  <h3 className="text-xl font-semibold text-juv-deep-chestnut mb-2">Need Brand Use Approval?</h3>
                  <p className="text-juv-deep-chestnut/70 mb-6">
                    Contact our team for guidelines on using JUV brand assets in your materials.
                  </p>
                  <Button className="apple-button-primary">
                    <Mail className="w-4 h-4 mr-2" />
                    Request Approval
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Footer CTA with Last Updated */}
        <section className="py-16 bg-juv-deep-chestnut">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-2xl mx-auto">
              <Heart className="w-12 h-12 text-juv-soft-blush mx-auto mb-6 animate-glow-pulse" />
              <h2 className="text-3xl font-bold text-white mb-4">Want to collaborate with JUV?</h2>
              <p className="text-white/80 mb-8">
                Let's create something beautiful together. Reach out to discuss partnership opportunities.
              </p>
              <Button size="lg" className="apple-button-primary bg-juv-terracotta hover:bg-juv-rose-chestnut text-white mb-8">
                Contact Us
              </Button>
              <div className="border-t border-white/20 pt-6">
                <p className="text-white/60 text-sm">
                  Last Updated: {currentDate}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default BrandKit;
